import { useEffect, useState } from "react";

import { ArrowButton, Button, DatePicker, Modal, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createKnowledgeCheck, editKnowledgeCheck, getKnowledgeCheckById } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { KnowledgeCheck, KnowledgeCheckValidationScheme } from "../../models/KnowledgeCheck";
import { codebookRouteKnowledgeCheck } from "../../routes/routes";
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookKnowledgeCheckEdit(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { handleChange } = useInput();


  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [knowledgeCheck, setKnowledgeCheck] = useState<KnowledgeCheck>({ name: "", canDelete: true });

  useEffect(() => {
    if (!location || !location.state || !(location.state as any).knowledgeCheckId) {
      setLoading(false);
      return;
    }
    const getKnowledgeCheck = async () => {
      var result = await getKnowledgeCheckById((location.state as any).knowledgeCheckId);
      setKnowledgeCheck(result);
      setLoading(false);
    }
    getKnowledgeCheck();
  }, [location]);


  const [errors, doValidations] = useValidation({
    data: knowledgeCheck,
    validationSchema: KnowledgeCheckValidationScheme,
  });

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (knowledgeCheck.dateTo) {
      knowledgeCheck.dateTo = new Date(Date.UTC(knowledgeCheck.dateTo.getFullYear(), knowledgeCheck.dateTo.getMonth(), knowledgeCheck.dateTo.getDate()));
    }

    if (knowledgeCheck.id) {
      await editKnowledgeCheck(knowledgeCheck.id, knowledgeCheck);
    }
    else {
      await createKnowledgeCheck(knowledgeCheck);
    }
    navigateToList();
  }

  const cancel = () => {
    navigateToList();
  }

  const navigateToList = () => {
    history.push(
      codebookRouteKnowledgeCheck(),
      {
        page: "list"
      }
    );
  }

  return (
    <>
      <Row>
        <Col md={9}>
          <ArrowButton backwards ariaLabel="" onClick={navigateToList} />
          <span className={styles.backButtonCaption}>
            {t("codeBook.returnToList")}
          </span>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col md={8}>
          <h3>{t("codeBook.knowledgeCheck")}</h3>
        </Col>
        <Col md={4} className="d-flex justify-content-end align-items-center">
          <Button outline onClick={() => cancel()}>
            {t("common.cancel")}
          </Button>
          <Button className="ml-3" onClick={() => saveChanges()}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      {!loading &&
        <Row className="mt-5">
          <Col md={8}>
            <TextField
              name={nameof<typeof knowledgeCheck>("name")}
              label={t("common.name")}
              errorText={errors?.name}
              value={knowledgeCheck.name}
              maxLength={80}
              onChange={handleChange(setKnowledgeCheck)}
            />
          </Col>
          <Col md={4} >
            <DatePicker
              value={knowledgeCheck.dateTo}
              label={t("common.dateTo")}
              name={nameof<typeof knowledgeCheck>("dateTo")}
              onChange={handleChange(setKnowledgeCheck)}
              isClearable={true}
              minYear={new Date().getFullYear() - 25}
              maxYear={new Date().getFullYear() + 25}
            />
          </Col>
        </Row>
      }
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}